import {AgentsIcon16} from '@github-ui/agent-sessions/components/AgentsIcon'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {
  type PropsWithPartialAnchor,
  type ReactPartialAnchorProps,
  useExternalAnchor,
} from '@github-ui/react-core/react-partial-anchor'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {
  CodeIcon,
  CodespacesIcon,
  type Icon,
  IssueOpenedIcon,
  OrganizationIcon,
  PeopleIcon,
  PersonAddIcon,
  PlusIcon,
  ProjectIcon,
  RepoIcon,
  RepoPushIcon,
  SparkleIcon,
} from '@primer/octicons-react'
import {ActionList, ActionMenu, Spinner} from '@primer/react'
import {Tooltip} from '@primer/react/next'
import {type ComponentProps, lazy, Suspense, useCallback, useEffect, useId, useRef, useState} from 'react'

import type {CreateIssueDialogProps} from './CreateIssueDialog'
import styles from './GlobalCreateMenu.module.css'

const LazyCreateIssueDialog = lazy(() => import('./CreateIssueDialog'))

interface GlobalCreateMenuBaseProps extends ReactPartialAnchorProps {
  showCreateRepo?: boolean
  showImportRepo?: boolean
  showCreateOrg?: boolean
  showCodespaces?: boolean
  showSpark?: boolean
  showCodingAgent?: boolean
  showGist?: boolean
  showCreateIssue?: boolean
  org?: {
    login: string
    addWord: string
  } | null
  owner?: string
  repo?: string
  side?: ComponentProps<typeof ActionMenu.Overlay>['side']
  isOpen?: boolean
  setIsOpen?: (value: boolean) => void
  environment?: CreateIssueDialogProps['environment']
}

interface LegacyProjectProps {
  showCreateLegacyProject?: true
  showCreateProject?: false
  createProjectUrl?: string
}

interface ProjectProps {
  showCreateLegacyProject?: false
  showCreateProject: true
  createProjectUrl: string
}

export type GlobalCreateMenuProps = GlobalCreateMenuBaseProps & (LegacyProjectProps | ProjectProps)

interface CreateMenuItemProps {
  label: string
  LeadingVisual: Icon
  analyticsKey: string
  analyticsLabel?: string
  itemRef?: React.RefObject<HTMLAnchorElement | null> | null
}

function analyticsPayload({
  analyticsKey,
  analyticsLabel,
}: Pick<CreateMenuItemProps, 'analyticsKey' | 'analyticsLabel'>) {
  const globalNavBarFeatureEnabled = isFeatureEnabled('global_nav_react')
  return {
    category: globalNavBarFeatureEnabled ? 'GLOBAL_NAV_BAR:CREATE_MENU' : 'SiteHeaderComponent',
    action: globalNavBarFeatureEnabled ? analyticsKey : 'app_dropdown',
    label: globalNavBarFeatureEnabled ? undefined : analyticsLabel,
  }
}

function CreateMenuLinkItem({
  label,
  href,
  LeadingVisual,
  analyticsKey,
  analyticsLabel,
  itemRef = null,
}: CreateMenuItemProps & {
  href: string
}) {
  const {sendClickAnalyticsEvent} = useClickAnalytics()

  const handleClick = useCallback(() => {
    sendClickAnalyticsEvent(analyticsPayload({analyticsKey, analyticsLabel}))
  }, [analyticsKey, analyticsLabel, sendClickAnalyticsEvent])

  return (
    <ActionList.LinkItem href={href} onClick={handleClick} ref={itemRef}>
      <ActionList.LeadingVisual>
        <LeadingVisual />
      </ActionList.LeadingVisual>
      {label}
    </ActionList.LinkItem>
  )
}

function CreateMenuItem({
  label,
  onClick,
  LeadingVisual,
  analyticsLabel,
  analyticsKey,
}: CreateMenuItemProps & {
  onClick: () => void
}) {
  const {sendClickAnalyticsEvent} = useClickAnalytics()

  const handleClick = useCallback(() => {
    sendClickAnalyticsEvent(analyticsPayload({analyticsKey, analyticsLabel}))
    onClick()
  }, [analyticsKey, analyticsLabel, sendClickAnalyticsEvent, onClick])

  return (
    <ActionList.Item onSelect={handleClick}>
      <ActionList.LeadingVisual>
        <LeadingVisual />
      </ActionList.LeadingVisual>
      {label}
    </ActionList.Item>
  )
}

function MenuItemSpinner() {
  return <Spinner size="small" />
}

function GlobalCreateMenuOverlay({
  side = 'outside-bottom',
  showCreateRepo,
  showImportRepo,
  showCreateOrg,
  showCreateProject,
  createProjectUrl,
  showCreateLegacyProject,
  showCreateIssue,
  showCodespaces,
  showSpark,
  showCodingAgent,
  showGist,
  org,
  owner,
  repo,
  isOpen = false,
  // eslint-disable-next-line @eslint-react/no-unstable-default-props
  setIsOpen = () => {},
  environment,
  anchorRef,
}: GlobalCreateMenuProps & {anchorRef?: React.RefObject<HTMLElement | null>}) {
  const [isCreateIssueVisible, setIsCreateIssueVisible] = useState(false)
  // Has the `LazyCreateIssueDialog` loaded? We use this to know when to enable the "New issue" menu item
  const [isCreateIssueLoaded, setIsCreateIssueLoaded] = useState(false)
  // Defer lazy loading the CreateIssueDialog until the first time the menu is opened
  const [createIssueIntent, setCreateIssueIntent] = useState(isOpen)
  useEffect(() => {
    if (isOpen) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCreateIssueIntent(true)
    }
  }, [isOpen])
  // this allows us to fallback to a spinner on the "New issue" menu item while the dialog is lazy-loading in,
  // but *only* after the user has clicked
  const shouldShowCreateIssueFallback = isCreateIssueVisible && !isCreateIssueLoaded
  const listRef = useRef<HTMLAnchorElement | null>(null)
  return (
    <>
      {showCreateIssue && createIssueIntent && (
        <Suspense>
          <LazyCreateIssueDialog
            isVisible={isCreateIssueVisible}
            setIsVisible={setIsCreateIssueVisible}
            setIsLoaded={setIsCreateIssueLoaded}
            setIsParentMenuOpen={setIsOpen}
            owner={owner}
            repo={repo}
            environment={environment}
            returnFocusRef={anchorRef ?? listRef}
          />
        </Suspense>
      )}
      <ActionMenu.Overlay side={side}>
        <ActionList>
          {showCreateIssue && (
            <CreateMenuItem
              label="New issue"
              analyticsLabel="new issue"
              analyticsKey="NEW_ISSUE"
              onClick={() => {
                setIsCreateIssueVisible(true)
                return false
              }}
              itemRef={listRef}
              LeadingVisual={shouldShowCreateIssueFallback ? MenuItemSpinner : IssueOpenedIcon}
            />
          )}
          {showCreateRepo !== false && (
            <CreateMenuLinkItem
              label="New repository"
              analyticsLabel="new repository"
              analyticsKey="NEW_REPOSITORY"
              href="/new"
              LeadingVisual={RepoIcon}
            />
          )}
          {showImportRepo && (
            <CreateMenuLinkItem
              label="Import repository"
              analyticsLabel="import repository"
              analyticsKey="IMPORT_REPOSITORY"
              href="/new/import"
              LeadingVisual={RepoPushIcon}
            />
          )}

          {(showSpark || showCodingAgent || showCodespaces || showGist) && <ActionList.Divider />}

          {showSpark && (
            <CreateMenuLinkItem
              label="New spark"
              analyticsLabel="new spark"
              analyticsKey="NEW_SPARK"
              href="/spark"
              LeadingVisual={SparkleIcon}
            />
          )}

          {showCodingAgent && (
            <CreateMenuLinkItem
              label="New agent session"
              analyticsLabel="new agent task"
              analyticsKey="NEW_AGENT_TASK"
              href="/copilot/agents"
              LeadingVisual={AgentsIcon16}
            />
          )}

          {(showCodespaces || showGist) && (
            <>
              {showCodespaces && (
                <CreateMenuLinkItem
                  label="New codespace"
                  analyticsLabel="new codespace"
                  analyticsKey="NEW_CODESPACE"
                  href="/codespaces/new"
                  LeadingVisual={CodespacesIcon}
                />
              )}
              {showGist && (
                <CreateMenuLinkItem
                  label="New gist"
                  analyticsLabel="new gist"
                  analyticsKey="NEW_GIST"
                  href="/gist"
                  LeadingVisual={CodeIcon}
                />
              )}
            </>
          )}

          {(showCreateOrg || showCreateProject || showCreateLegacyProject) && (
            <>
              <ActionList.Divider />
              {showCreateOrg && (
                <CreateMenuLinkItem
                  label="New organization"
                  analyticsLabel="new organization"
                  analyticsKey="NEW_ORGANIZATION"
                  href="/account/organizations/new"
                  LeadingVisual={OrganizationIcon}
                />
              )}
              {showCreateProject && (
                <CreateMenuLinkItem
                  label="New project"
                  analyticsLabel="new project"
                  analyticsKey="NEW_PROJECT"
                  href={createProjectUrl}
                  LeadingVisual={ProjectIcon}
                />
              )}
              {!showCreateProject && showCreateLegacyProject && (
                <CreateMenuLinkItem
                  label="New project"
                  analyticsLabel="new legacy project"
                  analyticsKey="NEW_LEGACY_PROJECT"
                  href="/new/project"
                  LeadingVisual={ProjectIcon}
                />
              )}
            </>
          )}

          {org && (
            <>
              <ActionList.Divider />
              <CreateMenuLinkItem
                label={`${org.addWord} someone to ${org.login}`}
                analyticsLabel="invite someone"
                analyticsKey="INVITE_SOMEONE"
                href={`/orgs/${org.login}/people#invite-member`}
                LeadingVisual={PersonAddIcon}
              />
              <CreateMenuLinkItem
                label={`New team in ${org.login}`}
                analyticsLabel="new team"
                analyticsKey="NEW_TEAM"
                href={`/orgs/${org.login}/new-team`}
                LeadingVisual={PeopleIcon}
              />
              <CreateMenuLinkItem
                label={`New repository in ${org.login}`}
                analyticsLabel="organization - new repository"
                analyticsKey="NEW_ORG_REPOSITORY"
                href={`/organizations/${org.login}/repositories/new`}
                LeadingVisual={RepoIcon}
              />
            </>
          )}
        </ActionList>
      </ActionMenu.Overlay>
    </>
  )
}

function ExternallyAnchoredGlobalCreateMenu(props: PropsWithPartialAnchor<GlobalCreateMenuProps>) {
  const {ref: anchorRef, open, setOpen} = useExternalAnchor(props.reactPartialAnchor)

  return (
    <ActionMenu anchorRef={anchorRef} open={open} onOpenChange={setOpen}>
      <GlobalCreateMenuOverlay {...props} isOpen={open} setIsOpen={setOpen} anchorRef={anchorRef} />
    </ActionMenu>
  )
}

function GlobalCreateMenuWithAnchor(props: GlobalCreateMenuProps) {
  const toolTipId = `global-create-menu-tooltip-${useId()}`
  const [isOpen, setIsOpen] = useState(false)
  const {sendClickAnalyticsEvent} = useClickAnalytics()

  return (
    <ActionMenu open={isOpen} onOpenChange={setIsOpen}>
      <Tooltip text="Create New..." type="label" id={toolTipId}>
        <ActionMenu.Button
          variant="invisible"
          className={styles.actionMenuButton}
          leadingVisual={PlusIcon}
          onClick={() =>
            sendClickAnalyticsEvent(
              analyticsPayload({analyticsKey: 'CREATE_MENU_TOGGLE', analyticsLabel: 'create new'}),
            )
          }
        />
      </Tooltip>
      <GlobalCreateMenuOverlay {...props} isOpen={isOpen} setIsOpen={setIsOpen} />
    </ActionMenu>
  )
}

export function GlobalCreateMenu(props: GlobalCreateMenuProps) {
  if (props.reactPartialAnchor) {
    return <ExternallyAnchoredGlobalCreateMenu {...props} reactPartialAnchor={props.reactPartialAnchor} />
  }

  return <GlobalCreateMenuWithAnchor {...props} />
}

export function GlobalCreateMenuItem(props: GlobalCreateMenuProps) {
  const [isOpen, setIsOpen] = useState(false)

  const handleOnSelect = () => {
    setIsOpen(open => !open)
  }

  return (
    <>
      <ActionMenu open={isOpen} onOpenChange={setIsOpen}>
        <ActionMenu.Anchor>
          <ActionList.Item onSelect={handleOnSelect} className={styles.hideOnSmall}>
            <ActionList.LeadingVisual>
              <PlusIcon />
            </ActionList.LeadingVisual>
            Create new
          </ActionList.Item>
        </ActionMenu.Anchor>

        <GlobalCreateMenuOverlay {...props} isOpen={isOpen} setIsOpen={setIsOpen} side="outside-top" />
      </ActionMenu>
      <ActionList.Divider className={styles.hideOnSmall} />
    </>
  )
}

try{ LazyCreateIssueDialog.displayName ||= 'LazyCreateIssueDialog' } catch {}
try{ CreateMenuLinkItem.displayName ||= 'CreateMenuLinkItem' } catch {}
try{ CreateMenuItem.displayName ||= 'CreateMenuItem' } catch {}
try{ MenuItemSpinner.displayName ||= 'MenuItemSpinner' } catch {}
try{ GlobalCreateMenuOverlay.displayName ||= 'GlobalCreateMenuOverlay' } catch {}
try{ ExternallyAnchoredGlobalCreateMenu.displayName ||= 'ExternallyAnchoredGlobalCreateMenu' } catch {}
try{ GlobalCreateMenuWithAnchor.displayName ||= 'GlobalCreateMenuWithAnchor' } catch {}
try{ GlobalCreateMenu.displayName ||= 'GlobalCreateMenu' } catch {}
try{ GlobalCreateMenuItem.displayName ||= 'GlobalCreateMenuItem' } catch {}